import React, { useCallback, useState, JSX, useEffect } from 'react';
import { useEditorState } from '@tiptap/react';
import { Popper } from '@mui/material';
import { usePopover } from 'hooks/common/usePopover';

import { MenuProps } from '../types';
import { LinkPreviewPanel } from '../../panels/LinkPreviewPanel';
import { LinkEditorPanel } from '../../panels';

export const LinkMenu = ({ editor, appendTo }: MenuProps): JSX.Element => {
  const [showEdit, setShowEdit] = useState(false);
  const { link, target } = useEditorState({
    editor,
    selector: (ctx) => {
      const attrs = ctx.editor.getAttributes('link');
      return { link: attrs.href, target: attrs.target };
    },
  });

  const { anchorEl, open, openPopover, closePopover } =
    usePopover<HTMLElement>();

  const shouldShow = useCallback(() => {
    const isActive = editor.isActive('link');
    return isActive;
  }, [editor]);

  const handleEdit = useCallback(() => {
    setShowEdit(true);
  }, []);

  const onSetLink = useCallback(
    (url: string, openInNewTab?: boolean) => {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url, target: openInNewTab ? '_blank' : '' })
        .run();
      setShowEdit(false);
    },
    [editor]
  );

  const onUnsetLink = useCallback(() => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setShowEdit(false);
    closePopover();
    return null;
  }, [editor, closePopover]);

  // Update menu position when link state changes
  const updateMenuPosition = useCallback(() => {
    if (
      !editor.isActive ||
      !editor.view ||
      !shouldShow() ||
      !appendTo?.current
    ) {
      closePopover();
      return;
    }

    // Get the DOM node of the link
    const linkPos = editor.state.selection.$from.pos;
    const domNode = editor.view.nodeDOM(linkPos) as HTMLElement;

    // Use the appendTo container directly
    openPopover(appendTo.current);

    if (!domNode) {
      // If we can't find the DOM node directly, use selection
      const domSelection = window.getSelection();
      if (!domSelection || domSelection.rangeCount === 0) {
        closePopover();
        return;
      }

      const range = domSelection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      if (rect.width === 0 && rect.height === 0) {
        closePopover();
        return;
      }

      // Set position data attributes for custom positioning
      if (anchorEl) {
        const popoverElement = anchorEl as HTMLElement;
        popoverElement.dataset.x = `${rect.left + rect.width / 2}`;
        popoverElement.dataset.y = `${rect.top}`;
      }
    } else {
      // If we have the DOM node, still use appendTo but position relative to domNode
      const rect = domNode.getBoundingClientRect();

      // Set position data attributes for custom positioning
      if (anchorEl) {
        const popoverElement = anchorEl as HTMLElement;
        popoverElement.dataset.x = `${rect.left + rect.width / 2}`;
        popoverElement.dataset.y = `${rect.top}`;
      }
    }
  }, [editor, shouldShow, openPopover, closePopover, appendTo, anchorEl]);

  // Set up event listeners
  useEffect(() => {
    const onUpdate = () => {
      if (shouldShow()) {
        updateMenuPosition();
      } else {
        closePopover();
      }
    };

    editor.on('update', onUpdate);
    editor.on('selectionUpdate', onUpdate);

    return () => {
      editor.off('update', onUpdate);
      editor.off('selectionUpdate', onUpdate);
    };
  }, [editor, updateMenuPosition, shouldShow, closePopover]);

  // Run once to check initial state
  useEffect(() => {
    if (shouldShow()) {
      updateMenuPosition();
    }
  }, [shouldShow, updateMenuPosition]);

  return (
    <Popper
      open={open && shouldShow()}
      anchorEl={anchorEl}
      placement='bottom'
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 10],
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top', 'right', 'left'],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 8,
          },
        },
      ]}
      style={{
        zIndex: 9999,
      }}
      container={appendTo?.current}
    >
      {showEdit ? (
        <LinkEditorPanel
          initialUrl={link}
          initialOpenInNewTab={target === '_blank'}
          onSetLink={onSetLink}
        />
      ) : (
        <LinkPreviewPanel
          url={link}
          onClear={onUnsetLink}
          onEdit={handleEdit}
        />
      )}
    </Popper>
  );
};

export default LinkMenu;
